import React, { useEffect, useMemo, useRef, useState } from 'react';
import DisplayPage, { PageBlock, PageModel } from '../components/DisplayPage';
import ListEditor from '../components/list-editor';
import { getPage, listPages, savePage, deletePage, renamePage } from '../lib/pages';
import { Link } from 'react-router-dom';
import Toast from '../components/Toast';
import { authStatus, setCachedAuthUser, AUTH_CACHE_KEY, authLogout, getCachedAuthUser, type AuthUser } from '../lib/auth';
import DimensionPickerModal from '../components/DimensionPickerModal';
import { useUIStore } from '../store/uiStore';

const cls = {
  btn: 'inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
  primary: 'bg-gray-900 text-white hover:bg-gray-800',
  ghost: 'border border-gray-200 hover:bg-gray-50',
  tile: 'rounded-xl border border-gray-200 bg-white shadow-sm',
  input: 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500',
  label: 'text-xs font-medium text-gray-600',
};

const newId = () => Math.random().toString(36).slice(2, 8);

const starter: PageModel = {
  title: 'New Page',
  rows: 1,
  columns: 1,
  gapX: 16,
  gapY: 16,
  radius: 12,
  // 16:9 aspect for a 1024px canvas => 1024 * 9 / 16 = 576
  targetHeightPx: 576,
  blocks: [],
};

const PagesEditor: React.FC = () => {
  const chromeHidden = !!useUIStore((s) => (s as any).chromeHidden);
  const setChromeHidden = useUIStore((s) => (s as any).setChromeHidden);
  const [page, setPage] = useState<PageModel>(starter);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = useMemo(() => page.blocks.find(b => b.id === selectedId) || null, [page, selectedId]);
  const [toast, setToast] = useState<{ message: string; type: 'success'|'error'|'info' }|null>(null);
  const [pages, setPages] = useState<{ slug: string; title?: string; size: number; mtime: number }[]>([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [currentSlug, setCurrentSlug] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [renameData, setRenameData] = useState<{ slug: string; title: string; newSlug: string; newTitle: string }|null>(null);
  const [showGridHelp, setShowGridHelp] = useState(false);
  const [showInnerHelpers, setShowInnerHelpers] = useState(true);
  const [showGapHandles, setShowGapHandles] = useState(true);
  const [confirmSectionDelete, setConfirmSectionDelete] = useState<number | null>(null);
  const [history, setHistory] = useState<PageModel[]>([]);
  const [future, setFuture] = useState<PageModel[]>([]);
  const [libraryListEditor, setLibraryListEditor] = useState<{ orientation: 'vertical'|'horizontal' } | null>(null);
  const [dimensionPickerFor, setDimensionPickerFor] = useState<number | null>(null);
  // Auth user for top-right chip
  const [authUser, setAuthUser] = useState<AuthUser | null>(() => getCachedAuthUser());
  // Section canvas refs to measure available width for aspect-ratio height calculations
  const sectionCanvasRefs = useRef<Record<number, HTMLDivElement | null>>({});
  // Per-section zoom factors (editor-only)
  const [zoomBySection, setZoomBySection] = useState<Record<number, number>>({});
  const getZoomAt = (i: number) => Math.max(0.25, Math.min(3, zoomBySection[i] ?? 1));
  const setZoomAt = (i: number, z: number) => setZoomBySection(prev => ({ ...prev, [i]: Math.max(0.25, Math.min(3, Number.isFinite(z) ? z : 1)) }));
  // Fixed preview canvas width to keep sections uniform regardless of sidebar resizing
  const PREVIEW_CANVAS_WIDTH = 1024; // px
  // Track measured container widths for each section to clamp zoom and avoid overflow
  const [containerWByIndex, setContainerWByIndex] = useState<Record<number, number>>({});
  // Effective zoom is clamped so scaled width never exceeds the available container width
  const getEffectiveZoomAt = (i: number) => {
    const desired = getZoomAt(i);
    const cw = containerWByIndex[i] || 0;
    if (cw <= 0) return desired;
    const fitByWidth = cw / PREVIEW_CANVAS_WIDTH;
    // If container is narrower, clamp down. If it's wider, auto-fit up to fill.
    const eff = desired > fitByWidth ? fitByWidth : fitByWidth; // always fit to width to eliminate right gap
    return Math.max(0.25, Math.min(3, eff));
  };

  // Resizable sidebar width (xl screens only)
  const [sidebarW, setSidebarW] = useState<number>(360);
  const RESIZER_W = 8;
  const MIN_SIDEBAR = 200;
  const MAX_SIDEBAR = 640;
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [prevSidebarW, setPrevSidebarW] = useState<number>(360);
  const [isXL, setIsXL] = useState<boolean>(false);
  const dividerRef = useRef<HTMLDivElement | null>(null);
  // UI: open section type dropdown index
  const [openTypeFor, setOpenTypeFor] = useState<number | null>(null);
  const [dividerX, setDividerX] = useState<number>(0);
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1280px)');
    const update = () => setIsXL(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  const onResizerDown = (e: React.MouseEvent) => {
    if (!isXL) return;
    e.preventDefault();
    // If collapsed, expand to previous width before dragging
    if (collapsed) {
      setCollapsed(false);
      setSidebarW(w => Math.max(MIN_SIDEBAR, Math.min(MAX_SIDEBAR, prevSidebarW || w)));
      dragRef.current = { startX: e.clientX, startW: Math.max(MIN_SIDEBAR, Math.min(MAX_SIDEBAR, prevSidebarW || sidebarW)) };
    } else {
      dragRef.current = { startX: e.clientX, startW: sidebarW };
    }
    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current; if (!d) return;
      const dx = ev.clientX - d.startX;
      const next = Math.max(MIN_SIDEBAR, Math.min(MAX_SIDEBAR, Math.round(d.startW + dx)));
      setSidebarW(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      dragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp, { once: true });
  };

  const effectiveSidebarW = collapsed ? 0 : sidebarW;
  const isNarrow = isXL && effectiveSidebarW <= 260 && effectiveSidebarW > 0;

  useEffect(() => {
    const update = () => {
      if (dividerRef.current) {
        const r = dividerRef.current.getBoundingClientRect();
        setDividerX(r.left + r.width / 2);
      }
      // Measure available widths for each section to clamp zoom
      const next: Record<number, number> = {};
      const entries = Object.entries(sectionCanvasRefs.current);
      for (const [key, el] of entries) {
        if (!el) continue;
        try {
          const parent = el.parentElement as HTMLElement | null;
          const w = (parent?.clientWidth ?? el.clientWidth) || 0; // clientWidth excludes scrollbar
          next[Number(key)] = Math.max(0, Math.round(w));
        } catch {}
      }
      setContainerWByIndex(prev => {
        // Only update state if something changed to avoid extra renders
        const same = JSON.stringify(prev) === JSON.stringify(next);
        return same ? prev : next;
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [effectiveSidebarW, isXL, collapsed]);

  const toggleCollapsed = () => {
    if (collapsed) {
      setCollapsed(false);
      setSidebarW(w => Math.max(MIN_SIDEBAR, Math.min(MAX_SIDEBAR, prevSidebarW || w)));
    } else {
      setPrevSidebarW(sidebarW);
      setCollapsed(true);
    }
  };

  // Cross-tab/session guard: re-verify when tab becomes visible or auth cache changes
  useEffect(() => {
    async function recheck() {
      const s = await authStatus();
      if (!s.ok) {
        try {
          const cached = getCachedAuthUser();
          if (cached) {
            setAuthUser(cached);
            setToast({ message: 'Session check failed. Staying in editor (using cached session).', type: 'info' });
            return;
          }
        } catch {}
        setToast({ message: 'Session check failed. Please refresh or sign in again if actions stop working.', type: 'info' });
        return;
      }
      if (s.user) {
        try { setCachedAuthUser(s.user); } catch {}
        setAuthUser(s.user as AuthUser);
      }
    }
    function onVisibility() {
      if (document.visibilityState === 'visible') recheck();
    }
    function onStorage(e: StorageEvent) {
      if (e.key === AUTH_CACHE_KEY) recheck();
    }
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('storage', onStorage);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    return () => {
      try { setChromeHidden(false); } catch {}
    };
  }, [setChromeHidden]);

  // On mount, hydrate from cache and then verify
  useEffect(() => {
    try { const cached = getCachedAuthUser(); if (cached) setAuthUser(cached); } catch {}
    (async () => {
      const s = await authStatus();
      if (s.ok && s.user) { setAuthUser(s.user as AuthUser); try { setCachedAuthUser(s.user); } catch {} }
      else { setAuthUser(null); }
    })();
  }, []);

  async function onSignOut() {
    try { await authLogout(); } finally { window.location.href = '/admin'; }
  }

  const clone = (p: PageModel) => JSON.parse(JSON.stringify(p)) as PageModel;

  // History grouping: batch multiple updates from one gesture into a single undo step
  const groupingRef = useRef(false);
  const basePrevRef = useRef<PageModel | null>(null);
  const groupChangedRef = useRef(false);
  const beginHistoryGroup = () => {
    if (!groupingRef.current) {
      groupingRef.current = true;
      basePrevRef.current = clone(page);
      groupChangedRef.current = false;
    }
  };
  const commitHistoryGroup = () => {
    if (groupingRef.current) {
      const prev = basePrevRef.current;
      basePrevRef.current = null;
      groupingRef.current = false;
      if (prev && groupChangedRef.current) {
        setHistory(h => {
          const last = h[h.length - 1];
          if (last && JSON.stringify(last) === JSON.stringify(prev)) return h; // avoid duplicate entry
          return [...h.slice(-49), prev];
        });
        setFuture([]);
      }
      groupChangedRef.current = false;
    }
  };

  const setAndRecord = (updater: (p: PageModel) => PageModel) => {
    // Snapshot whether we're currently in a history group at CALL time to avoid race conditions
    // where commitHistoryGroup may flip groupingRef before this updater executes.
    const isGrouped = groupingRef.current;
    setPage(p => {
      const prev = clone(p);
      const np = updater(p);
      const changed = JSON.stringify(prev) !== JSON.stringify(np);
      if (!changed) return p; // skip no-op updates entirely
      if (isGrouped) {
        groupChangedRef.current = true;
      } else {
        setHistory(h => {
          const last = h[h.length - 1];
          if (last && JSON.stringify(last) === JSON.stringify(prev)) return h; // avoid duplicate entry
          return [...h.slice(-49), prev];
        });
        setFuture([]);
      }
      return np;
    });
  };

  const canUndo = history.length > 0;
  const canRedo = future.length > 0;

  const undo = () => {
    setHistory(h => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setFuture(f => [clone(page), ...f].slice(0, 50));
      setPage(prev);
      return h.slice(0, -1);
    });
  };

  const redo = () => {
    setFuture(f => {
      if (f.length === 0) return f;
      const next = f[0];
      setHistory(h => [...h.slice(-49), clone(page)]);
      setPage(next);
      return f.slice(1);
    });
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      if (e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo) undo();
      } else if (e.key.toLowerCase() === 'z' && e.shiftKey) {
        e.preventDefault();
        if (canRedo) redo();
      } else if (e.key.toLowerCase() === 'y') {
        e.preventDefault();
        if (canRedo) redo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canUndo, canRedo, page]);

  const refreshPages = async () => {
    setLoadingPages(true);
    try {
      const items = await listPages();
      setPages(items);
    } finally {
      setLoadingPages(false);
    }
  };

  const doDelete = async (slug: string) => {
    setConfirmDelete(null);
    try {
      const ok = await deletePage(slug);
      if (ok) {
        setToast({ message: `Deleted ${slug}.json`, type: 'success' });
        if (currentSlug === slug) { setCurrentSlug(null); }
        refreshPages();
      } else {
        setToast({ message: 'Failed to delete page', type: 'error' });
      }
    } catch (e: any) {
      setToast({ message: `Failed to delete: ${e?.message || e}` , type: 'error' });
    }
  };

  const doRename = async () => {
    if (!renameData) return;
    const { slug, newSlug, newTitle } = renameData;
    setRenameData(null);
    const ns = newSlug.toLowerCase().trim().replace(/\s+/g,'-').replace(/[^a-z0-9\-]/g,'');
    if (!ns) { setToast({ message: 'Invalid target slug', type: 'error' }); return; }
    try {
      const ok = await renamePage(slug, ns, newTitle);
      if (ok) {
        setToast({ message: `Renamed to ${ns}.json`, type: 'success' });
        if (currentSlug === slug) setCurrentSlug(ns);
        refreshPages();
      } else {
        setToast({ message: 'Failed to rename page', type: 'error' });
      }
    } catch (e: any) {
      setToast({ message: `Failed to rename: ${e?.message || e}`, type: 'error' });
    }
  };

  useEffect(() => { refreshPages(); }, []);

  const clampBlocks = (rows: number, cols: number, blocks: PageBlock[]) =>
    blocks.map(b => {
      const w = Math.min(b.w, cols);
      const h = Math.min(b.h, rows);
      const x = Math.min(Math.max(1, b.x), Math.max(1, cols - w + 1));
      const y = Math.min(Math.max(1, b.y), Math.max(1, rows - h + 1));
      return { ...b, x, y, w, h };
    });

  // ----- Sections (multi-section pages) -----
  type Section = any; // matches single-section PageModel shape used by DisplayPage
  const getSections = (): Section[] => {
    const anyPage: any = page as any;
    if (Array.isArray(anyPage.sections)) return anyPage.sections as Section[];
    // Back-compat: treat top-level as implicit first section when no sections field exists
    const { title, rows, columns, gapX, gapY, radius, colFractions, rowFractions, merges, blocks, rowGaps, colGaps, layoutHeight, rowHeightsPx, targetHeightPx, sectionType } = anyPage;
    return [{ title, rows, columns, gapX, gapY, radius, colFractions, rowFractions, merges, blocks, rowGaps, colGaps, layoutHeight, rowHeightsPx, targetHeightPx, sectionType: sectionType || 'content' }];
  };
  const setSections = (next: Section[]) => {
    setAndRecord(p => ({ ...(p as any), sections: next } as any));
  };
  const removeSectionAt = (index: number) => {
    const base = getSections();
    const next = base.filter((_, i) => i !== index);
    setSections(next);
    setConfirmSectionDelete(null);
    setToast({ message: 'Section removed', type: 'success' });
  };
  const updateSection = (index: number, updater: (s: Section) => Section) => {
    const base = getSections();
    const next = base.map((s, i) => (i === index ? updater(s) : s));
    setSections(next);
  };
  const ensureSections = () => {
    const anyPage: any = page as any;
    if (Array.isArray(anyPage.sections) && anyPage.sections.length > 0) return;
    const base = getSections();
    setSections(base);
  };
  const addSection = () => {
    const base = getSections();
    const tpl = base[base.length - 1] || {};
    const fresh: Section = {
      title: `${tpl.title || page.title || 'Section'} ${base.length + 1}`,
      rows: tpl.rows ?? 1,
      columns: tpl.columns ?? 1,
      gapX: tpl.gapX ?? 16,
      gapY: tpl.gapY ?? 16,
      radius: tpl.radius ?? 12,
      colFractions: tpl.colFractions ? [...tpl.colFractions] : undefined,
      rowFractions: tpl.rowFractions ? [...tpl.rowFractions] : undefined,
      merges: [],
      // default to 16:9 if not inheriting
      targetHeightPx: tpl.targetHeightPx ?? 576,
      sectionType: tpl.sectionType || 'content',
      blocks: [],
    };
    setSections([...base, fresh]);
  };

  // Per-section field updaters
  const changeRowsAt = (i: number, r: number) => {
    const rows = Math.max(1, Math.min(24, Math.floor(r)));
    updateSection(i, (p: any) => {
      const needGaps = Math.max(0, rows - 1);
      const baseGaps = Array.isArray(p.rowGaps) ? [...p.rowGaps] : [];
      const rg = [...baseGaps];
      if (rg.length > needGaps) rg.length = needGaps;
      while (rg.length < needGaps) rg.push(p.gapY ?? 16);
      return { ...p, rows, rowHeightsPx: undefined, rowFractions: undefined, rowGaps: rg, blocks: clampBlocks(rows, p.columns || 12, p.blocks || []) };
    });
  };
  const changeColsAt = (i: number, c: number) => {
    const columns = Math.max(1, Math.min(24, Math.floor(c)));
    updateSection(i, (p: any) => {
      const needGaps = Math.max(0, columns - 1);
      const baseGaps = Array.isArray(p.colGaps) ? [...p.colGaps] : [];
      const cg = [...baseGaps];
      if (cg.length > needGaps) cg.length = needGaps;
      while (cg.length < needGaps) cg.push(p.gapX ?? 16);
      return { ...p, columns, colFractions: undefined, colGaps: cg, blocks: clampBlocks(p.rows || 12, columns, p.blocks || []) };
    });
  };
  const changeGapXAt = (i: number, v: number) => updateSection(i, (p: any) => ({ ...p, gapX: Math.max(0, Math.min(64, Math.floor(v))) }));
  const changeGapYAt = (i: number, v: number) => updateSection(i, (p: any) => ({ ...p, gapY: Math.max(0, Math.min(64, Math.floor(v))) }));
  const changeRadiusAt = (i: number, v: number) => updateSection(i, (p: any) => ({ ...p, radius: Math.max(0, Math.min(32, Math.floor(v))) }));

  const onDropNewAt = (i: number, type: PageBlock['type'], x: number, y: number) => {
    const b: PageBlock = { id: newId(), type, x, y, w: 1, h: 1, content: (type === 'text' ? '<p>New text block</p>' : type === 'cta' ? 'Call to action' : ''), ...(type === 'listV' || type === 'listH' ? { listItems: [] as string[] } : {}), url: (type !== 'text' && type !== 'cta' && type !== 'listV' && type !== 'listH') ? '/uploads/sample.jpg' : undefined };
    updateSection(i, (p: any) => ({ ...p, blocks: [ ...(p.blocks || []), b ] }));
    setSelectedId(b.id);
  };
  const onMoveBlockAt = (i: number, id: string, x: number, y: number) => {
    updateSection(i, (p: any) => ({ ...p, blocks: (p.blocks || []).map((b: PageBlock) => b.id === id ? { ...b, x, y } : b) }));
    setSelectedId(id);
  };
  const onGridSizesChangeAt = (i: number, cols: number[], rows: number[], layoutHeight: number, rowHeightsPx: number[]) => {
    updateSection(i, (p: any) => {
      const sameCols = JSON.stringify(cols) === JSON.stringify(p.colFractions || []);
      const sameRows = JSON.stringify(rows) === JSON.stringify(p.rowFractions || []);
      const sameH = p.layoutHeight === layoutHeight;
      const sameHeights = JSON.stringify(rowHeightsPx) === JSON.stringify(p.rowHeightsPx || []);
      const nextColumns = Math.max(1, cols.length || (p.columns || 1));
      const nextRows = Math.max(1, rowHeightsPx.length || (p.rows || 1));
      if (sameCols && sameRows && sameH && sameHeights && nextColumns === (p.columns || 1) && nextRows === (p.rows || 1)) return p;
      return { ...p, colFractions: cols, rowFractions: rows, layoutHeight, rowHeightsPx, columns: nextColumns, rows: nextRows };
    });
  };
  const onGridGapsChangeAt = (i: number, colGaps: number[], rowGaps: number[]) => {
    updateSection(i, (p: any) => {
      const sameCols = JSON.stringify(colGaps) === JSON.stringify(p.colGaps || []);
      const sameRows = JSON.stringify(rowGaps) === JSON.stringify(p.rowGaps || []);
      if (sameCols && sameRows) return p;
      return { ...p, colGaps, rowGaps };
    });
  };
  const onGridMergesChangeAt = (i: number, merges: { r: number; c: number; rs: number; cs: number }[]) => {
    updateSection(i, (p: any) => {
      const same = JSON.stringify(merges) === JSON.stringify(p.merges || []);
      if (same) return p;
      return { ...p, merges };
    });
  };

  const changeRows = (r: number) => {
    const rows = Math.max(1, Math.min(24, Math.floor(r)));
    setAndRecord(p => {
      // Normalize row gaps to length rows-1
      const needGaps = Math.max(0, rows - 1);
      const baseGaps = Array.isArray(p.rowGaps) ? [...p.rowGaps] : [];
      const rg = [...baseGaps];
      if (rg.length > needGaps) rg.length = needGaps; // trim
      while (rg.length < needGaps) rg.push(p.gapY ?? 16); // pad with default
      // Reset row sizes/fractions so DisplayPage rebuilds tracks from the new count
      const next: PageModel = {
        ...p,
        rows,
        rowHeightsPx: undefined,
        rowFractions: undefined,
        rowGaps: rg,
        blocks: clampBlocks(rows, p.columns || 12, p.blocks),
      };
      return next;
    });
  };
  const changeCols = (c: number) => {
    const columns = Math.max(1, Math.min(24, Math.floor(c)));
    setAndRecord(p => {
      // Normalize column gaps to length columns-1
      const needGaps = Math.max(0, columns - 1);
      const baseGaps = Array.isArray(p.colGaps) ? [...p.colGaps] : [];
      const cg = [...baseGaps];
      if (cg.length > needGaps) cg.length = needGaps; // trim
      while (cg.length < needGaps) cg.push(p.gapX ?? 16); // pad with default
      // Reset column fractions so DisplayPage rebuilds columns from the new count
      const next: PageModel = {
        ...p,
        columns,
        colFractions: undefined,
        colGaps: cg,
        blocks: clampBlocks(p.rows || 12, columns, p.blocks),
      };
      return next;
    });
  };

  const changeGapX = (v: number) => {
    const gapX = Math.max(0, Math.min(64, Math.floor(v)));
    setAndRecord(p => ({ ...p, gapX }));
  };
  const changeGapY = (v: number) => {
    const gapY = Math.max(0, Math.min(64, Math.floor(v)));
    setAndRecord(p => ({ ...p, gapY }));
  };
  const changeRadius = (v: number) => {
    const radius = Math.max(0, Math.min(32, Math.floor(v)));
    setAndRecord(p => ({ ...p, radius }));
  };

  const addBlock = (type: PageBlock['type']) => {
    const b: PageBlock = {
      id: newId(),
      type,
      x: 1,
      y: (page.blocks.reduce((m, b) => Math.max(m, b.y + b.h - 1), 0) || 0) + 1,
      w: type === 'hero' ? 12 : type === 'listH' ? 8 : 6,
      h: type === 'hero' ? 6 : type === 'cta' ? 2 : type === 'listH' ? 2 : 3,
      content: (
        type === 'text' ? '<p>New text block</p>' :
        type === 'cta' ? 'Call to action' :
        ''
      ),
      ...(type === 'listV' || type === 'listH' ? { listItems: [] as string[] } : {}),
      url: (type !== 'text' && type !== 'cta' && type !== 'listV' && type !== 'listH') ? '/uploads/sample.jpg' : undefined,
    };
    setAndRecord(p => ({ ...p, blocks: [...p.blocks, b] }));
    setSelectedId(b.id);
  };

  const onDropNew = (type: PageBlock['type'], x: number, y: number) => {
    // Default new block as 1x1 in the dropped cell
    const b: PageBlock = {
      id: newId(),
      type,
      x,
      y,
      w: 1,
      h: 1,
      content: (
        type === 'text' ? '<p>New text block</p>' :
        type === 'cta' ? 'Call to action' :
        ''
      ),
      ...(type === 'listV' || type === 'listH' ? { listItems: [] as string[] } : {}),
      url: (type !== 'text' && type !== 'cta' && type !== 'listV' && type !== 'listH') ? '/uploads/sample.jpg' : undefined,
    };
    setAndRecord(p => ({ ...p, blocks: [...p.blocks, b] }));
    setSelectedId(b.id);
  };

  const onMoveBlock = (id: string, x: number, y: number) => {
    setAndRecord(p => ({
      ...p,
      blocks: p.blocks.map(b => b.id === id ? { ...b, x, y } : b)
    }));
    setSelectedId(id);
  };

  const onGridSizesChange = (cols: number[], rows: number[], layoutHeight: number, rowHeightsPx: number[]) => {
    // Compare inside updater to avoid stale outer 'page' and only record if something actually changed
    setAndRecord(p => {
      const sameCols = JSON.stringify(cols) === JSON.stringify(p.colFractions || []);
      const sameRows = JSON.stringify(rows) === JSON.stringify(p.rowFractions || []);
      const sameH = (p as any).layoutHeight === layoutHeight;
      const sameHeights = JSON.stringify(rowHeightsPx) === JSON.stringify((p as any).rowHeightsPx || []);
      const nextColumns = Math.max(1, cols.length || (p.columns || 1));
      const nextRows = Math.max(1, rowHeightsPx.length || (p.rows || 1));
      if (sameCols && sameRows && sameH && sameHeights && nextColumns === (p.columns || 1) && nextRows === (p.rows || 1)) return p;
      return { ...p, colFractions: cols, rowFractions: rows, layoutHeight, rowHeightsPx, columns: nextColumns, rows: nextRows } as PageModel;
    });
  };

  const onGridGapsChange = (colGaps: number[], rowGaps: number[]) => {
    const sameCols = JSON.stringify(colGaps) === JSON.stringify(page.colGaps || []);
    const sameRows = JSON.stringify(rowGaps) === JSON.stringify(page.rowGaps || []);
    if (sameCols && sameRows) return;
    setAndRecord(p => ({ ...p, colGaps, rowGaps }));
  };

  const onGridMergesChange = (merges: { r: number; c: number; rs: number; cs: number }[]) => {
    // Compare against the latest state inside the updater to avoid stale-closure issues
    setAndRecord(p => {
      const same = JSON.stringify(merges) === JSON.stringify(p.merges || []);
      if (same) return p;
      return { ...p, merges };
    });
  };

  const doSave = async () => {
    let slug = (page.title || '').toLowerCase().trim().replace(/\s+/g,'-').replace(/[^a-z0-9\-]/g,'');
    if (!slug) {
      slug = window.prompt('Enter a page slug (letters, numbers, hyphen):', '') || '';
      slug = slug.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9\-]/g,'');
    }
    if (!slug) { setToast({ message: 'Save cancelled: missing slug', type: 'info' }); return; }
    try {
      await savePage(slug, page);
      setCurrentSlug(slug);
      setToast({ message: `Saved ${slug}.json`, type: 'success' });
      refreshPages();
    } catch (e: any) {
      setToast({ message: `Failed to save: ${e?.message || e}`, type: 'error' });
    }
  };

  const openPreview = () => {
    try {
      const id = Math.random().toString(36).slice(2, 10);
      const snapshot = { page };
      localStorage.setItem(`page_preview_${id}`, JSON.stringify(snapshot));
      const url = `/admin/page-preview?id=${encodeURIComponent(id)}`;
      window.open(url, '_blank', 'noreferrer');
    } catch {}
  };

  const removeSelected = () => {
    if (!selected) return;
    setAndRecord(p => ({ ...p, blocks: p.blocks.filter(b => b.id !== selected.id) }));
    setSelectedId(null);
  };

  const updateSelected = (patch: Partial<PageBlock>) => {
    if (!selected) return;
    setAndRecord(p => ({
      ...p,
      blocks: p.blocks.map(b => b.id === selected.id ? { ...b, ...patch } : b),
    }));
  };

  const updateBlockById = (id: string, patch: Partial<PageBlock>) => {
    setAndRecord(p => ({
      ...p,
      blocks: p.blocks.map(b => b.id === id ? { ...b, ...patch } : b),
    }));
  };

  const removeBlockById = (id: string) => {
    setAndRecord(p => ({
      ...p,
      blocks: p.blocks.filter(b => b.id !== id),
    }));
    if (selectedId === id) setSelectedId(null);
  };

  const onReorder = (dir: -1 | 1) => {
    if (!selected) return;
    setAndRecord(p => {
      const idx = p.blocks.findIndex(b => b.id === selected.id);
      if (idx < 0) return p;
      const arr = [...p.blocks];
      const j = Math.min(arr.length - 1, Math.max(0, idx + dir));
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...p, blocks: arr };
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/70 backdrop-blur border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm"
              title="Back to Admin"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              <span className="text-sm font-semibold">Admin</span>
            </Link>
            <span className="font-semibold text-gray-900">Pages</span>
            <button
              type="button"
              onClick={() => setChromeHidden(!chromeHidden)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm transition"
              title={chromeHidden ? 'Show header and navigation bar' : 'Hide header and navigation bar'}
              aria-label={chromeHidden ? 'Show header and navigation bar' : 'Hide header and navigation bar'}
            >
              {chromeHidden ? (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 15l-6-6-6 6" />
                </svg>
              )}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={page.title}
              onChange={e => setAndRecord(p => ({ ...p, title: e.target.value }))}
              className={cls.input}
              placeholder="Page title"
              style={{ width: 240 }}
            />
            <div className="flex items-center gap-1">
              <button
                className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Undo (Ctrl/Cmd+Z)"
                onClick={undo}
                disabled={!canUndo}
              >
                <svg className="h-4 w-4 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 14l-5-5 5-5"/><path d="M4 9h10a7 7 0 010 14h-1"/></svg>
              </button>
              <button
                className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Redo (Ctrl/Cmd+Y or Shift+Z)"
                onClick={redo}
                disabled={!canRedo}
              >
                <svg className="h-4 w-4 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 14l5-5-5-5"/><path d="M20 9H10a7 7 0 100 14h1"/></svg>
              </button>
            </div>
            <button className={`${cls.btn} ${cls.primary} gap-2`} onClick={doSave} title="Save page">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 3h10l4 4v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/>
                <path d="M7 3v6h8V3"/>
                <path d="M7 13h10v8"/>
              </svg>
              <span>Save</span>
            </button>
            <button className={`${cls.btn} border border-gray-200 hover:bg-gray-50 gap-2`} onClick={openPreview} title="Preview in new tab">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              <span>Preview</span>
            </button>
            {authUser && (
              <div className="ml-2 hidden sm:flex items-center gap-2 rounded-full border border-gray-200 px-2 py-1 bg-white">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-purple-100 text-purple-700 text-xs font-semibold uppercase">
                  {(authUser.username || 'U').slice(0,1)}
                </span>
                <span className="text-gray-800 text-sm">{authUser.name || authUser.username}</span>
                {authUser.role && <span className="text-xs text-gray-500">({authUser.role})</span>}
                <button onClick={onSignOut} className="ml-1 h-7 px-2 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm">Sign out</button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 xl:grid-cols-[360px_8px_minmax(0,1fr)] gap-6"
        style={isXL ? { gridTemplateColumns: `${effectiveSidebarW}px ${RESIZER_W}px minmax(0,1fr)` } : undefined}
      >
        {/* Left controls */}
        <aside className={`${cls.tile} ${isNarrow ? 'p-3' : 'p-4'} h-max sticky top-20 space-y-5 ${collapsed ? 'pointer-events-none opacity-0' : ''}`}>
          <div className="space-y-3">
            <div className="font-semibold text-gray-900">Add block</div>
            <div className="space-y-3">
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-2 tracking-wide">Media</div>
                <div className={`grid ${isNarrow ? 'grid-cols-1' : 'grid-cols-2'} gap-2`}>
                  <button
                    className="group relative rounded-xl border border-blue-200 bg-blue-50/60 hover:bg-blue-50 text-blue-800 px-3 py-3 shadow-sm inline-flex items-center justify-between gap-2"
                    draggable
                    onDragStart={e => e.dataTransfer.setData('text/plain', 'new:image')}
                    title="Drag to a cell to add Image"
                  >
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white text-blue-600 ring-1 ring-blue-200">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 15l4-4 4 4 5-5 4 4"/></svg>
                    </span>
                    <span className="text-sm font-medium">Image</span>
                    <span
                      className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-blue-600 ring-1 ring-blue-200 cursor-move"
                      draggable
                      onDragStart={e => e.dataTransfer.setData('text/plain', 'new:image')}
                      title="Drag to a cell"
                    >
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6h6M9 12h6M9 18h6"/></svg>
                    </span>
                  </button>
                  <button
                    className="group relative rounded-xl border border-indigo-200 bg-indigo-50/60 hover:bg-indigo-50 text-indigo-800 px-3 py-3 shadow-sm inline-flex items-center justify-between gap-2"
                    draggable
                    onDragStart={e => e.dataTransfer.setData('text/plain', 'new:hero')}
                    title="Drag to a cell to add Hero"
                  >
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white text-indigo-600 ring-1 ring-indigo-200">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 5h18v14H3z"/><path d="M3 12h18"/><path d="M7 12v7"/></svg>
                    </span>
                    <span className="text-sm font-medium">Hero</span>
                    <span
                      className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-indigo-600 ring-1 ring-indigo-200 cursor-move"
                      draggable
                      onDragStart={e => e.dataTransfer.setData('text/plain', 'new:hero')}
                      title="Drag to a cell"
                    >
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6h6M9 12h6M9 18h6"/></svg>
                    </span>
                  </button>
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-2 tracking-wide">Content</div>
                <div className={`grid ${isNarrow ? 'grid-cols-1' : 'grid-cols-2'} gap-2`}>
                  <button
                    className="group relative rounded-xl border border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50 text-emerald-800 px-3 py-3 shadow-sm inline-flex items-center justify-between gap-2"
                    draggable
                    onDragStart={e => e.dataTransfer.setData('text/plain', 'new:text')}
                    title="Drag to a cell to add Text"
                  >
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white text-emerald-600 ring-1 ring-emerald-200">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16"/><path d="M4 10h10"/><path d="M4 14h14"/><path d="M4 18h8"/></svg>
                    </span>
                    <span className="text-sm font-medium">Text</span>
                    <span
                      className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-emerald-600 ring-1 ring-emerald-200 cursor-move"
                      draggable
                      onDragStart={e => e.dataTransfer.setData('text/plain', 'new:text')}
                      title="Drag to a cell"
                    >
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6h6M9 12h6M9 18h6"/></svg>
                    </span>
                  </button>
                  <button
                    className="group relative rounded-xl border border-amber-200 bg-amber-50/60 hover:bg-amber-50 text-amber-800 px-3 py-3 shadow-sm inline-flex items-center justify-between gap-2"
                    draggable
                    onDragStart={e => e.dataTransfer.setData('text/plain', 'new:cta')}
                    title="Drag to a cell to add CTA"
                  >
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white text-amber-600 ring-1 ring-amber-200">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="7" width="18" height="10" rx="2"/><path d="M7 12h10"/></svg>
                    </span>
                    <span className="text-sm font-medium">CTA</span>
                    <span
                      className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-amber-600 ring-1 ring-amber-200 cursor-move"
                      draggable
                      onDragStart={e => e.dataTransfer.setData('text/plain', 'new:cta')}
                      title="Drag to a cell"
                    >
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6h6M9 12h6M9 18h6"/></svg>
                    </span>
                  </button>
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-2 tracking-wide">Lists</div>
                <div className={`grid ${isNarrow ? 'grid-cols-1' : 'grid-cols-2'} gap-2`}>
                  <button
                    className="group relative rounded-xl border border-teal-200 bg-teal-50/60 hover:bg-teal-50 text-teal-800 px-3 py-3 shadow-sm inline-flex items-center justify-between gap-2"
                    draggable
                    onDragStart={e => e.dataTransfer.setData('text/plain', 'new:listV')}
                    title="Drag to a cell to add List (V)"
                  >
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white text-teal-600 ring-1 ring-teal-200">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="6" cy="7" r="1.5"/><circle cx="6" cy="12" r="1.5"/><circle cx="6" cy="17" r="1.5"/><path d="M10 7h8"/><path d="M10 12h8"/><path d="M10 17h8"/></svg>
                    </span>
                    <span className="text-sm font-medium">List (V)</span>
                    <span
                      className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-teal-600 ring-1 ring-teal-200 cursor-move"
                      draggable
                      onDragStart={e => e.dataTransfer.setData('text/plain', 'new:listV')}
                      title="Drag to a cell"
                    >
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6h6M9 12h6M9 18h6"/></svg>
                    </span>
                  </button>
                  <button
                    className="group relative rounded-xl border border-pink-200 bg-pink-50/60 hover:bg-pink-50 text-pink-800 px-3 py-3 shadow-sm inline-flex items-center justify-between gap-2"
                    draggable
                    onDragStart={e => e.dataTransfer.setData('text/plain', 'new:listH')}
                    title="Drag to a cell to add List (H)"
                  >
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white text-pink-600 ring-1 ring-pink-200">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="7" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="17" cy="12" r="1.5"/></svg>
                    </span>
                    <span className="text-sm font-medium">List (H)</span>
                    <span
                      className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-pink-600 ring-1 ring-pink-200 cursor-move"
                      draggable
                      onDragStart={e => e.dataTransfer.setData('text/plain', 'new:listH')}
                      title="Drag to a cell"
                    >
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6h6M9 12h6M9 18h6"/></svg>
                    </span>
                  </button>
                  <button
                    className={`${isNarrow ? '' : 'col-span-2'} h-9 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50`}
                    title="Open List Editor"
                    onClick={() => setLibraryListEditor({ orientation: 'vertical' })}
                  >
                    <svg className="h-4 w-4 mr-2 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><circle cx="3" cy="6" r="1"/><circle cx="3" cy="12" r="1"/><circle cx="3" cy="18" r="1"/></svg>
                    Manage Lists
                  </button>
                </div>
              </div>
            </div>
          </div>

          

          <div>
            <div className="font-semibold text-gray-900 mb-2">Pages</div>
            <div className="rounded-lg border border-gray-200 bg-white max-h-64 overflow-auto divide-y divide-gray-100">
              {loadingPages ? (
                <div className="px-3 py-2 text-sm text-gray-500">Loading pages…</div>
              ) : pages.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No pages yet</div>
              ) : (
                pages.sort((a,b)=>b.mtime-a.mtime).map(it => (
                  <div key={it.slug} className={`flex items-center justify-between gap-2 px-3 py-2 ${currentSlug===it.slug ? 'bg-purple-50' : ''}`}>
                    <button
                      className="flex-1 text-left text-sm hover:underline"
                      onClick={async () => { const p = await getPage(it.slug); if (p) { setPage(p); setSelectedId(null); setCurrentSlug(it.slug); setHistory([]); setFuture([]); } }}
                    >
                      <div className="font-medium text-gray-900">{it.title || it.slug}</div>
                      <div className="text-xs text-gray-500">{new Date(it.mtime*1000).toLocaleString()}</div>
                    </button>
                    <div className="flex items-center gap-1">
                      <button
                        className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-gray-100"
                        title="Open published page in new tab"
                        onClick={() => window.open(`/page/${encodeURIComponent(it.slug)}`, '_blank', 'noreferrer')}
                      >
                        <svg className="h-4 w-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                          <path d="M15 3h6v6"/>
                          <path d="M10 14L21 3"/>
                        </svg>
                      </button>
                      <button
                        className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-gray-100"
                        title="Rename"
                        onClick={() => setRenameData({ slug: it.slug, title: it.title || '', newSlug: it.slug, newTitle: it.title || '' })}
                      >
                        <svg className="h-4 w-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                      </button>
                      <button
                        className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-red-50"
                        title="Delete"
                        onClick={() => setConfirmDelete(it.slug)}
                      >
                        <svg className="h-4 w-4 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          
        </aside>

        {/* Vertical helper line / resizer (only on xl) */}
        <div
          aria-hidden
          className="hidden xl:flex self-stretch items-center justify-center cursor-col-resize group relative"
          ref={dividerRef}
          onMouseDown={onResizerDown}
          title="Drag to resize panels"
        >
          <div className="h-full w-full rounded-full bg-gray-300/90 group-hover:bg-gray-400 transition relative">
            <div className="absolute inset-y-6 left-1/2 -translate-x-1/2 w-[2px] bg-white/80 rounded-full" />
          </div>
        </div>

        {/* Fixed-position collapse/expand button centered in viewport height */}
        <button
          className="hidden xl:inline-flex fixed top-1/2 -translate-y-1/2 -translate-x-1/2 h-7 w-7 items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm hover:bg-gray-50 text-gray-600 hover:text-gray-800 opacity-90 hover:opacity-100 z-30"
          style={{ left: Math.max(0, dividerX) }}
          onMouseDown={(e)=>{ e.stopPropagation(); }}
          onClick={(e)=>{ e.stopPropagation(); toggleCollapsed(); }}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7"/></svg>
          ) : (
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 5l-7 7 7 7"/></svg>
          )}
        </button>

        {/* Right preview: live */}
        <section className={`${cls.tile} overflow-visible`}>
          <div className="bg-gray-50">
            {getSections().map((sec, i) => (
              <div key={i} className="mb-8 rounded-xl border border-gray-200 bg-white shadow-sm overflow-visible">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between relative">
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Zoom</span>
                      <div className="inline-flex items-center rounded-lg border border-gray-200 overflow-hidden">
                        <button className="px-2 py-1 hover:bg-gray-50" onClick={() => setZoomAt(i, getZoomAt(i) - 0.1)}>−</button>
                        <div className="w-14 text-center select-none">{Math.round(getEffectiveZoomAt(i) * 100)}%</div>
                        <button className="px-2 py-1 hover:bg-gray-50" onClick={() => setZoomAt(i, getZoomAt(i) + 0.1)}>+</button>
                      </div>
                      <button
                        className="h-8 px-2 inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                        title="Reset zoom"
                        onClick={() => setZoomAt(i, 1)}
                      >
                        Reset
                      </button>
                    </div>
                    {/* Rows and Columns grouped inline */}
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">Rows</span>
                        <div className="inline-flex items-center rounded-lg border border-gray-200 overflow-hidden">
                          <button className="px-2 py-1 hover:bg-gray-50" onClick={() => changeRowsAt(i, (sec.rows||1)-1)}>−</button>
                          <input className="w-12 text-center outline-none" type="number" min={1} max={24} value={sec.rows || 1} onChange={e => changeRowsAt(i, Number(e.target.value || 1))} />
                          <button className="px-2 py-1 hover:bg-gray-50" onClick={() => changeRowsAt(i, (sec.rows||1)+1)}>+</button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600">Columns</span>
                        <div className="inline-flex items-center rounded-lg border border-gray-200 overflow-hidden">
                          <button className="px-2 py-1 hover:bg-gray-50" onClick={() => changeColsAt(i, (sec.columns||1)-1)}>−</button>
                          <input className="w-12 text-center outline-none" type="number" min={1} max={24} value={sec.columns || 1} onChange={e => changeColsAt(i, Number(e.target.value || 1))} />
                          <button className="px-2 py-1 hover:bg-gray-50" onClick={() => changeColsAt(i, (sec.columns||1)+1)}>+</button>
                        </div>
                      </div>
                    </div>
                    {/* Section Type dropdown moved after Columns */}
                    <div className="relative">
                      <button
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 hover:bg-gray-50 text-gray-800"
                        onClick={() => setOpenTypeFor(openTypeFor === i ? null : i)}
                        title="Section type"
                      >
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-br from-indigo-600 to-purple-600 text-white shadow-sm">
                          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 12h10M4 18h7"/></svg>
                        </span>
                        <span className="font-medium">Section Type:</span>
                        <span className="rounded-md bg-gray-100 px-2 py-0.5 text-gray-700">
                          {(sec as any).sectionType === 'heroFooter' ? 'Hero / Footer' : 'Content'}
                        </span>
                        <svg className="h-4 w-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                      </button>
                      {openTypeFor === i && (
                        <div className="absolute right-0 mt-2 z-50 w-48 rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
                          <button
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${((sec as any).sectionType ?? 'content') === 'content' ? 'bg-gray-50' : ''}`}
                            onClick={() => { updateSection(i, (p: any) => ({ ...p, sectionType: 'content' })); setOpenTypeFor(null); }}
                          >
                            <div className="flex items-center gap-2">
                              <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-blue-50 text-blue-600 border border-blue-200">
                                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="7" width="16" height="10" rx="2"/></svg>
                              </span>
                              <div>
                                <div className="font-medium text-gray-800">Content</div>
                                <div className="text-xs text-gray-500">Regular content section</div>
                              </div>
                            </div>
                          </button>
                          <button
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${((sec as any).sectionType ?? 'content') === 'heroFooter' ? 'bg-gray-50' : ''}`}
                            onClick={() => { updateSection(i, (p: any) => ({ ...p, sectionType: 'heroFooter' })); setOpenTypeFor(null); }}
                          >
                            <div className="flex items-center gap-2">
                              <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-purple-50 text-purple-600 border border-purple-200">
                                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l3 7h7l-5.6 4.1L18 21l-6-4-6 4 1.6-6.9L2 10h7z"/></svg>
                              </span>
                              <div>
                                <div className="font-medium text-gray-800">Hero / Footer</div>
                                <div className="text-xs text-gray-500">Prominent header or page footer</div>
                              </div>
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Gap X</span>
                      <div className="inline-flex items-center rounded-lg border border-gray-200 overflow-hidden">
                        <button className="px-2 py-1 hover:bg-gray-50" onClick={() => changeGapXAt(i, (sec.gapX||0)-4)}>−</button>
                        <input className="w-12 text-center outline-none" type="number" min={0} max={64} step={1} value={sec.gapX ?? 16} onChange={e => changeGapXAt(i, Number(e.target.value || 0))} />
                        <button className="px-2 py-1 hover:bg-gray-50" onClick={() => changeGapXAt(i, (sec.gapX||0)+4)}>+</button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Gap Y</span>
                      <div className="inline-flex items-center rounded-lg border border-gray-200 overflow-hidden">
                        <button className="px-2 py-1 hover:bg-gray-50" onClick={() => changeGapYAt(i, (sec.gapY||0)-4)}>−</button>
                        <input className="w-12 text-center outline-none" type="number" min={0} max={64} step={1} value={sec.gapY ?? 16} onChange={e => changeGapYAt(i, Number(e.target.value || 0))} />
                        <button className="px-2 py-1 hover:bg-gray-50" onClick={() => changeGapYAt(i, (sec.gapY||0)+4)}>+</button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Radius</span>
                      <div className="inline-flex items-center rounded-lg border border-gray-200 overflow-hidden">
                        <button className="px-2 py-1 hover:bg-gray-50" onClick={() => changeRadiusAt(i, (sec.radius||0)-2)}>−</button>
                        <input className="w-12 text-center outline-none" type="number" min={0} max={32} step={1} value={sec.radius ?? 12} onChange={e => changeRadiusAt(i, Number(e.target.value || 0))} />
                        <button className="px-2 py-1 hover:bg-gray-50" onClick={() => changeRadiusAt(i, (sec.radius||0)+2)}>+</button>
                      </div>
                    </div>
                    <button
                      className="h-8 px-2 inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-white hover:bg-indigo-50 text-indigo-700"
                      title="Set section dimensions"
                      onClick={() => setDimensionPickerFor(i)}
                    >
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-[4px] bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200">
                        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                      </span>
                      <span className="text-sm">Set Dimensions</span>
                    </button>
                    <button
                      className={`h-8 px-2 inline-flex items-center gap-2 rounded-md border ${showInnerHelpers ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-200 hover:bg-gray-50 text-gray-700'}`}
                      title={showInnerHelpers ? 'Hide inner helpers' : 'Show inner helpers'}
                      onClick={() => setShowInnerHelpers(v => !v)}
                    >
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-current">
                        <span className={`block h-2 w-2 rounded-full ${showInnerHelpers ? 'bg-blue-600' : 'bg-transparent'}`} />
                      </span>
                      <span className="text-sm">Inner lines</span>
                    </button>
                    <button
                      className={`h-8 px-2 inline-flex items-center gap-2 rounded-md border ${showGapHandles ? 'border-purple-200 bg-purple-50 text-purple-700' : 'border-gray-200 hover:bg-gray-50 text-gray-700'}`}
                      title={showGapHandles ? 'Hide spacing handles' : 'Show spacing handles'}
                      onClick={() => setShowGapHandles(v => !v)}
                    >
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-current">
                        <span className={`block h-2 w-2 rounded-full ${showGapHandles ? 'bg-purple-600' : 'bg-transparent'}`} />
                      </span>
                      <span className="text-sm">Gap handles</span>
                    </button>
                    <button
                      className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-200 hover:bg-blue-50"
                      title="Grid helpers info"
                      onClick={() => setShowGridHelp(true)}
                    >
                      <svg className="h-4 w-4 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 8h.01"/><path d="M11 12h1v4h1"/></svg>
                    </button>
                    <button
                      className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-red-200 bg-white hover:bg-red-50"
                      title="Delete this section"
                      onClick={(e)=>{ e.stopPropagation(); setConfirmSectionDelete(i); }}
                    >
                      <svg className="h-4 w-4 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                    </button>
                  </div>
                  {confirmSectionDelete === i && (
                    <div className="absolute right-3 top-12 z-[2000] w-64 rounded-lg border border-gray-200 bg-white shadow-lg">
                      <div className="px-3 py-2 border-b border-gray-100 font-medium text-gray-900">Delete section?</div>
                      <div className="p-3 text-sm text-gray-700">This will remove the entire section and its blocks. This action cannot be undone.</div>
                      <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-end gap-2">
                        <button className="h-8 px-3 rounded-md border border-gray-200 hover:bg-gray-50 text-sm" onClick={()=> setConfirmSectionDelete(null)}>Cancel</button>
                        <button className="h-8 px-3 rounded-md bg-red-600 text-white hover:bg-red-700 text-sm" onClick={()=> removeSectionAt(i)}>Delete</button>
                      </div>
                    </div>
                  )}
                </div>
                {(() => {
                  const rowsCount = (Array.isArray(sec.rowFractions) && sec.rowFractions.length) ? sec.rowFractions.length : (sec.rows || 1);
                  const needGaps = Math.max(0, rowsCount - 1);
                  const rowGapsArr: number[] = (Array.isArray(sec.rowGaps) && sec.rowGaps.length === needGaps)
                    ? sec.rowGaps
                    : Array.from({ length: needGaps }, () => sec.gapY ?? 16);
                  const gapsSum = rowGapsArr.reduce((a,b)=>a+(b||0),0);
                  const fallbackH = (typeof sec.layoutHeight === 'number' ? sec.layoutHeight : 0) + gapsSum;
                  const targetH = Math.max(0, Math.round(sec.targetHeightPx || fallbackH || 0));
                  const cw = containerWByIndex[i] || 0;
                  const desired = getZoomAt(i);
                  const maxByWidth = cw > 0 ? cw / PREVIEW_CANVAS_WIDTH : desired;
                  const zoom = Math.max(0.25, Math.min(desired, Math.min(3, maxByWidth)));
                  return (
                    <div
                      ref={el => { sectionCanvasRefs.current[i] = el; }}
                      className="pb-4 overflow-auto"
                      style={targetH ? { height: `${Math.round(targetH * zoom)}px`, width: '100%', transition: 'width 150ms ease, height 150ms ease' } : { width: '100%', transition: 'width 150ms ease' }}
                    >
                      <div
                        style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', width: `${PREVIEW_CANVAS_WIDTH}px`, height: targetH ? `${targetH}px` : undefined, transition: 'transform 150ms ease' }}
                      >
                        <DisplayPage
                          page={sec}
                          compact
                          editable
                          onDropNew={(t,x,y)=> onDropNewAt(i,t,x,y)}
                          onMoveBlock={(id,x,y)=> onMoveBlockAt(i,id,x,y)}
                          onGridSizesChange={(c,r,h,px)=> onGridSizesChangeAt(i,c,r,h,px)}
                          onGridGapsChange={(cg,rg)=> onGridGapsChangeAt(i,cg,rg)}
                          onGridMergesChange={(m)=> onGridMergesChangeAt(i,m)}
                          showInnerHelpers={showInnerHelpers}
                          showGapHandles={showGapHandles}
                          beginHistoryGroup={beginHistoryGroup}
                          commitHistoryGroup={commitHistoryGroup}
                          onUpdateBlock={(id, patch)=> updateSection(i, (p: any)=> ({ ...p, blocks: (p.blocks||[]).map((b: PageBlock)=> b.id===id?{...b,...patch}:b) }))}
                          onRemoveBlock={(id)=> updateSection(i, (p: any)=> ({ ...p, blocks: (p.blocks||[]).filter((b: PageBlock)=> b.id!==id) }))}
                          onCellStylesChange={(maps)=> updateSection(i, (p: any)=> ({ ...p, ...maps }))}
                        />
                      </div>
                    </div>
                  );
                })()}
                
              </div>
            ))}

            <div className="py-6 flex items-center justify-center">
              <button onClick={() => { ensureSections(); addSection(); }} className="inline-flex items-center gap-3 rounded-full border border-purple-300 bg-white px-4 py-2 text-purple-700 hover:bg-purple-50 shadow-sm" title="Add a new section">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-blue-600 text-white shadow">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
                </span>
                <span className="font-medium">Add section</span>
              </button>
            </div>
          </div>
        </section>
        {/* Global List Library Editor (not tied to a block) */}
        <ListEditor
          open={!!libraryListEditor}
          orientation={(libraryListEditor?.orientation || 'vertical') as 'vertical'|'horizontal'}
          initialItems={[]}
          initialEntries={[]}
          onClose={()=> setLibraryListEditor(null)}
          onSave={() => setLibraryListEditor(null)}
        />
      </div>
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 w-full max-w-md">
            <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-900">Delete page</div>
            <div className="p-4 text-sm text-gray-700">Are you sure you want to delete <span className="font-medium">{confirmDelete}.json</span>? This action cannot be undone.</div>
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
              <button className={`${cls.btn} ${cls.ghost}`} onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className={`${cls.btn} ${cls.primary}`} onClick={() => doDelete(confirmDelete)}>Delete</button>
            </div>
          </div>
        </div>
      )}
      {renameData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 w-full max-w-md">
            <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-900">Rename page</div>
            <div className="p-4 space-y-3">
              <label className="block text-sm text-gray-700">New slug
                <input className={`${cls.input} mt-1`} value={renameData.newSlug} onChange={e => setRenameData(d => d ? { ...d, newSlug: e.target.value } : d)} />
              </label>
              <label className="block text-sm text-gray-700">Title
                <input className={`${cls.input} mt-1`} value={renameData.newTitle} onChange={e => setRenameData(d => d ? { ...d, newTitle: e.target.value } : d)} />
              </label>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
              <button className={`${cls.btn} ${cls.ghost}`} onClick={() => setRenameData(null)}>Cancel</button>
              <button className={`${cls.btn} ${cls.primary}`} onClick={doRename}>Save</button>
            </div>
          </div>
        </div>
      )}
      {showGridHelp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-blue-50">
                  <svg className="h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 8h.01"/><path d="M11 12h1v4h1"/></svg>
                </div>
                <div className="font-semibold text-gray-900">How grid helpers work</div>
              </div>
              <button className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-gray-100" onClick={() => setShowGridHelp(false)} aria-label="Close">
                <svg className="h-4 w-4 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="p-5 grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="font-medium text-gray-900">Resizing columns</div>
                <ul className="text-sm leading-6 text-gray-700 list-disc pl-5 space-y-1">
                  <li>Drag the <span className="text-purple-600 font-medium">vertical</span> helper line to change column widths.</li>
                  <li>By default, only the two columns around the line are adjusted.</li>
                  <li>All helpers snap to the center of the gap between cells.</li>
                </ul>
                <div className="mt-4 font-medium text-gray-900">Scale mode (columns)</div>
                <ul className="text-sm leading-6 text-gray-700 list-disc pl-5 space-y-1">
                  <li><span className="text-blue-600 font-medium">Double‑click</span> a vertical helper to toggle <span className="font-medium">Scale mode</span> (it turns blue).</li>
                  <li>Now dragging will scale <span className="font-medium">all columns to the left</span> of the helper proportionally, borrowing from the right side.</li>
                  <li>Click again to exit Scale mode.</li>
                </ul>
              </div>
              <div className="space-y-3">
                <div className="font-medium text-gray-900">Resizing rows</div>
                <ul className="text-sm leading-6 text-gray-700 list-disc pl-5 space-y-1">
                  <li>Drag the <span className="text-purple-600 font-medium">horizontal</span> helper line to change row heights.</li>
                  <li>Rows have a minimum height so content stays readable. If needed, the grid grows and pushes rows below.</li>
                </ul>
                <div className="mt-4 font-medium text-gray-900">Scale mode (rows)</div>
                <ul className="text-sm leading-6 text-gray-700 list-disc pl-5 space-y-1">
                  <li><span className="text-blue-600 font-medium">Double‑click</span> a horizontal helper to toggle <span className="font-medium">Scale mode</span>.</li>
                  <li>Now dragging will scale <span className="font-medium">all rows above</span> the helper proportionally, borrowing from the rows below.</li>
                </ul>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <div className="text-xs text-gray-500">Tip: Your custom sizes are saved with the page. You can always fine‑tune later.</div>
              <button className={`${cls.btn} ${cls.primary}`} onClick={() => setShowGridHelp(false)}>Got it</button>
            </div>
          </div>
        </div>
      )}
      <DimensionPickerModal
        open={dimensionPickerFor !== null}
        onClose={() => setDimensionPickerFor(null)}
        onApply={({ width, height }) => {
          if (dimensionPickerFor === null) return;
          const el = sectionCanvasRefs.current[dimensionPickerFor];
          let canvasW = Math.max(0, el?.clientWidth || el?.getBoundingClientRect()?.width || 0);
          if (el) {
            try {
              const cs = window.getComputedStyle(el);
              const padX = (parseFloat(cs.paddingLeft || '0') || 0) + (parseFloat(cs.paddingRight || '0') || 0);
              canvasW = Math.max(0, canvasW - padX);
            } catch {}
          }
          const aspect = height > 0 && width > 0 ? (height / width) : 0;
          const appliedH = aspect > 0 && canvasW > 0 ? Math.round(canvasW * aspect) : height;
          // Compute total row gaps so DisplayPage total height (rows + gaps) matches appliedH
          const curSections: any[] = getSections();
          const sec: any = curSections[dimensionPickerFor] || {};
          const rowsCount = (Array.isArray(sec.rowFractions) && sec.rowFractions.length) ? sec.rowFractions.length : (sec.rows || 1);
          const needGaps = Math.max(0, rowsCount - 1);
          const rowGapsArr: number[] = (() => {
            if (Array.isArray(sec.rowGaps) && sec.rowGaps.length === needGaps) return sec.rowGaps;
            const g = sec.gapY ?? 16; return Array.from({ length: needGaps }, () => g);
          })();
          const gapsSum = rowGapsArr.reduce((a,b)=>a+(b||0),0);
          const desiredLayoutHeight = Math.max(rowsCount * 64, appliedH - gapsSum);
          updateSection(dimensionPickerFor, (p: any) => ({
            ...p,
            layoutHeight: desiredLayoutHeight,
            rowHeightsPx: undefined, // let DisplayPage recompute row heights from layoutHeight
            targetWidthPx: Math.round(canvasW),
            targetHeightPx: desiredLayoutHeight + gapsSum,
          }));
          setDimensionPickerFor(null);
          setToast({ message: `Applied ${Math.round(canvasW)}×${appliedH} (ratio ${width}:${height})`, type: 'success' });
        }}
      />
      {toast && (
        <div className="fixed top-4 right-4 z-50 pointer-events-none">
          <div className="pointer-events-auto">
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PagesEditor;
